import React, { useCallback, useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon, Div, Col, Text } from '@pinpoint/design-kit';
import ReactTooltip from 'react-tooltip';

import BaseDialog from './base';
/* eslint-disable react/prop-types */

function ApiKeyModal({ isOpen, actions, apiKey }) {
  const [isCopied, setIsCopied] = useState(false);
  const clearCopied = useCallback(() => setIsCopied(false), [setIsCopied]);
  const setCopied = useCallback(() => setIsCopied(true), [setIsCopied]);
  const handleCopy = useCallback(() => {
    try {
      navigator.clipboard.writeText(apiKey);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
    setCopied();
    setTimeout(() => {
      clearCopied();
      actions.close.on();
    }, 750);
  }, [actions.close, apiKey, clearCopied, setCopied]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  }, [isCopied]);

  return (
    <BaseDialog
      hideCloseIcon
      isOpen={isOpen}
      actions={{
        cancel: { ...actions.cancel },
        close: { ...actions.close, before: clearCopied },
      }}
      title={{
        text: `Client API Key`,
        backgroundColor: 'marigold',
        color: 'white',
        textSize: 'title',
        icon: <Icon name="Key" color="white" m="0" size="3.125rem" viewBox="0 0 21 10" />,
      }}
      items={{
        content: [
          <Div
            key="rt-api-key-copy"
            textSize="paragraph"
            textWeight="500"
            rounded="md"
            border="1px solid"
            borderColor="shadeD9"
            h="5rem"
            d="flex"
            align="center"
            justify="space-between"
          >
            <Col m={{ t: '1rem', l: '1rem' }}>
              <Text p={{ l: '1.5rem' }}>{apiKey}</Text>
            </Col>
            <Col size="1">
              {isCopied ? (
                <Icon
                  name="Checked"
                  color="success800"
                  m={{ r: '0.5rem', t: '.5rem' }}
                  size="1.125rem"
                  viewBox="0 0 50 50"
                  data-tip="Copied!"
                  cursor="none"
                />
              ) : (
                <Icon
                  name="Copy"
                  color="shadeCC"
                  m={{ r: '0.5rem', t: '.5rem' }}
                  size="1.125rem"
                  viewBox="0 0 14 14"
                  onClick={handleCopy}
                  data-tip="Copy"
                  cursor="copy"
                />
              )}
            </Col>
          </Div>,
        ],
        buttons: [],
      }}
    />
  );
}

/* eslint-disable react/require-default-props */
ApiKeyModal.propTypes = {
  apiKey: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    cancel: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    close: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    handlers: PropTypes.arrayOf(
      PropTypes.shape({
        before: PropTypes.func,
        on: PropTypes.func.isRequired,
        after: PropTypes.func,
      })
    ),
  }).isRequired,
  title: PropTypes.shape({
    text: PropTypes.string.isRequired,
    textSize: PropTypes.string,
    backgroundColor: PropTypes.string.isRequired,
    color: PropTypes.string,
  }),
  items: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.element).isRequired,
    buttons: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        textSize: PropTypes.string,
        close: PropTypes.bool.isRequired,
      }).isRequired
    ),
  }),
};
/* eslint-enable */

export default ApiKeyModal;
